/**
 * Builds the Project P&L rows (same aggregate as the Project P&L dashboard)
 * for other Finances sections — Progress Billing today, CPA reporting later.
 *
 * Takes the `useProjectPLWorkspace()` output (QuickBooks payments / expenses,
 * design-fee payments) and lifts the per-deal proposal, change-order,
 * override, job-PO, and designer-invoice subscriptions the dashboard wires
 * inline. PM-excluded deals are dropped. The dashboard itself is untouched.
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useCRM } from '../utils/CRMContext';
import type { ChangeOrder, DealPLOverrides, Pipeline, ProposalBuilderData } from '../types';
import type { PurchaseRequisition } from '../types/purchasing';
import { subscribeChangeOrdersForDeal } from '../utils/changeOrders';
import { subscribeJobPurchaseRequisitions } from '../utils/purchasingRequisitions';
import { subscribeDealPLOverridesMap } from '../utils/dealPLOverrides';
import { subscribeApprovedDesignerInvoices, type DesignerInvoiceDoc } from '../utils/designerInvoiceService';
import { flattenApprovedDesignerInvoiceJobCash, qbExpenseDedupeSets } from '../utils/designerInvoicePL';
import { classifyPLDealGroup, pickPLDeals, type ProjectPLGroup } from '../utils/qbDealLinkOptions';
import { aggregateProjectPLBatch, type ProjectPLRow } from '../utils/projectPL';
import type { useProjectPLWorkspace } from './useProjectPLWorkspace';

type ProjectPLWorkspace = ReturnType<typeof useProjectPLWorkspace>;

export interface UseProjectPLRowsResult {
  /** Non-excluded Project P&L rows (active + finalized). */
  rows: ProjectPLRow[];
  /** Dashboard group per deal — Under Contract / Preconstruction / Estimating. */
  groupByDealId: Map<string, ProjectPLGroup>;
  /** False until the excluded-deal query and first subscriptions have answered. */
  ready: boolean;
}

function buildPipelineStageNameMap(pipelines: Pipeline[]): Record<string, string> {
  const map: Record<string, string> = {};
  pipelines.forEach(p => {
    p.stages.forEach(s => {
      map[s.id] = s.name;
    });
  });
  return map;
}

export function useProjectPLRows(
  workspace: Pick<ProjectPLWorkspace, 'canAccess' | 'qbPayments' | 'qbExpenses' | 'designFeePayments'>,
): UseProjectPLRowsResult {
  const { enhancedDeals, timberProjects, pipelines } = useCRM();
  const { canAccess, qbPayments, qbExpenses, designFeePayments } = workspace;

  const [proposalsByDeal, setProposalsByDeal] = useState<Record<string, ProposalBuilderData | null>>({});
  const [changeOrdersByDeal, setChangeOrdersByDeal] = useState<Record<string, ChangeOrder[]>>({});
  const [overridesByDeal, setOverridesByDeal] = useState<Record<string, DealPLOverrides | null>>({});
  const [jobPurchases, setJobPurchases] = useState<PurchaseRequisition[]>([]);
  const [approvedDesignerInvoices, setApprovedDesignerInvoices] = useState<DesignerInvoiceDoc[]>([]);
  const [excludedDealIds, setExcludedDealIds] = useState<string[]>([]);
  const [excludedReady, setExcludedReady] = useState(false);
  const [proposalsReady, setProposalsReady] = useState(false);

  const plDeals = useMemo(() => pickPLDeals(enhancedDeals, timberProjects), [enhancedDeals, timberProjects]);
  const plDealIds = useMemo(() => plDeals.map(d => d.id).filter(Boolean) as string[], [plDeals]);
  const plDealIdsKey = useMemo(() => plDealIds.slice().sort().join('|'), [plDealIds]);

  // Excluded deals — one collection query so we never subscribe to their proposals.
  useEffect(() => {
    if (!canAccess) return;
    if (plDealIds.length === 0) {
      setExcludedDealIds([]);
      setExcludedReady(true);
      return;
    }
    const idSet = new Set(plDealIds);
    const q = query(collection(db, 'dealPLOverrides'), where('excluded', '==', true));
    const unsub = onSnapshot(
      q,
      snap => {
        const next: string[] = [];
        snap.forEach(docSnap => {
          if (idSet.has(docSnap.id)) next.push(docSnap.id);
        });
        setExcludedDealIds(next);
        setExcludedReady(true);
      },
      err => {
        console.error('dealPLOverrides excluded query failed:', err);
        setExcludedDealIds([]);
        setExcludedReady(true);
      },
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess, plDealIdsKey]);

  const excludedSet = useMemo(() => new Set(excludedDealIds), [excludedDealIds]);
  const includedDealIds = useMemo(
    () => (excludedReady ? plDealIds.filter(id => !excludedSet.has(id)) : []),
    [plDealIds, excludedSet, excludedReady],
  );
  const includedKey = useMemo(() => includedDealIds.slice().sort().join('|'), [includedDealIds]);

  // Proposal budgets per deal.
  useEffect(() => {
    if (!canAccess) return;
    const ids = new Set(includedDealIds);
    setProposalsByDeal(prev => {
      const next: Record<string, ProposalBuilderData | null> = {};
      Object.entries(prev).forEach(([id, val]) => {
        if (ids.has(id)) next[id] = val;
      });
      return next;
    });
    if (includedDealIds.length === 0) {
      setProposalsReady(excludedReady);
      return;
    }
    setProposalsReady(false);
    let pending = includedDealIds.length;
    const settle = () => {
      pending -= 1;
      if (pending <= 0) setProposalsReady(true);
    };
    const seen = new Set<string>();
    const unsubs = includedDealIds.map(id =>
      onSnapshot(
        doc(db, 'proposalBuilders', id),
        snap => {
          setProposalsByDeal(prev => ({
            ...prev,
            [id]: snap.exists() ? ({ id: snap.id, ...(snap.data() as any) } as ProposalBuilderData) : null,
          }));
          if (!seen.has(id)) {
            seen.add(id);
            settle();
          }
        },
        () => {
          setProposalsByDeal(prev => ({ ...prev, [id]: null }));
          if (!seen.has(id)) {
            seen.add(id);
            settle();
          }
        },
      ),
    );
    return () => {
      unsubs.forEach(u => {
        try {
          u();
        } catch {
          /* ignore */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess, includedKey, excludedReady]);

  // Change orders per deal (budget deltas).
  useEffect(() => {
    if (!canAccess) return;
    const ids = new Set(includedDealIds);
    setChangeOrdersByDeal(prev => {
      const next: Record<string, ChangeOrder[]> = {};
      Object.entries(prev).forEach(([id, val]) => {
        if (ids.has(id)) next[id] = val;
      });
      return next;
    });
    const unsubs = includedDealIds.map(id =>
      subscribeChangeOrdersForDeal(id, orders => {
        setChangeOrdersByDeal(prev => ({ ...prev, [id]: orders }));
      }),
    );
    return () => {
      unsubs.forEach(u => {
        try {
          u();
        } catch {
          /* ignore */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess, includedKey]);

  // Bucket overrides / exclusions per deal.
  useEffect(() => {
    if (!canAccess) return;
    const unsub = subscribeDealPLOverridesMap(plDealIds, map => setOverridesByDeal(map));
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess, plDealIdsKey]);

  useEffect(() => {
    if (!canAccess) return;
    return subscribeJobPurchaseRequisitions(setJobPurchases);
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess) return;
    return subscribeApprovedDesignerInvoices(setApprovedDesignerInvoices);
  }, [canAccess]);

  // Skip invoices already on the P&L as their synced QuickBooks bill (qbExpenseId / qboBillId).
  const designerInvoiceJobCash = useMemo(
    () => flattenApprovedDesignerInvoiceJobCash(approvedDesignerInvoices, qbExpenseDedupeSets(qbExpenses)),
    [approvedDesignerInvoices, qbExpenses],
  );

  const overridesForPL = useMemo(() => {
    const o: Record<string, DealPLOverrides | null> = { ...overridesByDeal };
    excludedSet.forEach(id => {
      const prev = o[id];
      if (prev?.excluded === false) return;
      o[id] = prev ? { ...prev, dealId: id, excluded: true } : { dealId: id, excluded: true, expenseBuckets: {} };
    });
    return o;
  }, [overridesByDeal, excludedSet]);

  const groupByDealId = useMemo(() => {
    const map = new Map<string, ProjectPLGroup>();
    plDeals.forEach(d => {
      if (d.id) map.set(d.id, classifyPLDealGroup(d, timberProjects));
    });
    return map;
  }, [plDeals, timberProjects]);

  const pipelineStageNames = useMemo(() => {
    const names = buildPipelineStageNameMap(pipelines);
    plDeals.forEach(d => {
      if (!d.id || d.outcome === 'won') return;
      const group = groupByDealId.get(d.id);
      if (group === 'preconstruction') names[d.stageId] = 'Preconstruction';
      if (group === 'estimating') names[d.stageId] = 'Estimating';
    });
    return names;
  }, [pipelines, plDeals, groupByDealId]);

  const rows = useMemo<ProjectPLRow[]>(() => {
    if (!canAccess || !excludedReady) return [];
    return aggregateProjectPLBatch({
      deals: plDeals,
      proposalsByDeal,
      changeOrdersByDeal,
      jobPurchases,
      designerInvoiceJobCash,
      expenses: qbExpenses,
      payments: qbPayments,
      designFeePayments,
      overridesByDeal: overridesForPL,
      pipelineStageNames,
    }).filter(r => !r.excluded);
  }, [
    canAccess,
    excludedReady,
    plDeals,
    proposalsByDeal,
    changeOrdersByDeal,
    jobPurchases,
    designerInvoiceJobCash,
    qbExpenses,
    qbPayments,
    designFeePayments,
    overridesForPL,
    pipelineStageNames,
  ]);

  return { rows, groupByDealId, ready: canAccess && excludedReady && proposalsReady };
}
